import { Space } from '@components/Space/Space';
import Skeleton from '@pedidosya/web-fenix/atoms/Skeleton';
import SkeletonFenix from '@pedidosya/web-fenix/system/Skeleton';
import React, { FC, memo } from 'react';
import {
  ColumnSkeleton,
  ContainerSkeleton,
  ContainerSkeletonFood,
  DescriptionShopCard,
  MrSkeleton,
  RowSkeleton,
  ShopCardContainer,
  SubContainerSkeleton,
  SwiperContainer,
  WrapSkeletons,
} from './SkeletonFoodStyled';

interface IFoodSkeleton {
  index: number;
  isRevamp: boolean;
}

const SkeletonPreRevampFood = (index) => (
  <ContainerSkeleton key={index} data-testid="skeleton">
    <Space mt={12} />
    <SubContainerSkeleton>
      <MrSkeleton>
        <Skeleton height="12" shape="square" width="12" />
      </MrSkeleton>
      <WrapSkeletons>
        <Skeleton height="4" shape="square" width="71.75" />
        <Space mb={4} />
        <Skeleton height="4" shape="square" width="35.5" />
      </WrapSkeletons>
    </SubContainerSkeleton>
    <SwiperContainer>
      <RowSkeleton>
        <ShopCardContainer>
          <Skeleton height="29.25" shape="square" width="48.75" />
          <DescriptionShopCard>
            <Space mb={12} />
            <Skeleton height="4" shape="square" width="40" />
            <Space mb={16} />
            <ColumnSkeleton>
              <Skeleton height="4" shape="square" width="11.5" />
              <Space mt={4} />
              <Skeleton height="4" shape="square" width="18" />
            </ColumnSkeleton>
          </DescriptionShopCard>
        </ShopCardContainer>
        <ShopCardContainer>
          <Skeleton height="29.25" shape="square" width="48.75" />
          <DescriptionShopCard>
            <Space mb={12} />
            <Skeleton height="4" shape="square" width="40" />
            <Space mb={16} />
            <ColumnSkeleton>
              <Skeleton height="4" shape="square" width="11.5" />
              <Space mt={4} />
              <Skeleton height="4" shape="square" width="18" />
            </ColumnSkeleton>
          </DescriptionShopCard>
        </ShopCardContainer>
      </RowSkeleton>
    </SwiperContainer>
  </ContainerSkeleton>
);

const VendorRowSkeleton = () => (
  <div
    data-testid="skeleton-food-vendor-row-revamp"
    style={{
      display: 'flex',
      flexDirection: 'row',
      flexGrow: 1,
      padding: '24px 16px 16px 16px',
      justifyContent: 'space-between',
    }}
  >
    <div style={{ display: 'flex', gap: 12 }}>
      <div style={{ paddingRight: 12 }}>
        <SkeletonFenix
          borderRadiusSize="large"
          height="shape-size-action-14"
          width="shape-size-action-14"
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <SkeletonFenix borderRadiusSize="large" width="48.25" />
        <SkeletonFenix borderRadiusSize="large" width="34.25" />
      </div>
    </div>

    <SkeletonFenix
      borderRadiusSize="large"
      height="shape-size-action-05"
      width="shape-size-action-08"
    />
  </div>
);

const ProductCardSVerticalSkeleton = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      padding: '0 16px',
    }}
  >
    <div style={{ paddingBottom: 12 }}>
      <SkeletonFenix borderRadiusSize="large" height="31.75" width="31.75" />
    </div>

    <div style={{ paddingBottom: 16 }}>
      <SkeletonFenix
        borderRadiusSize="medium"
        height="shape-size-action-05"
        width="18.75"
      />
    </div>

    <div style={{ paddingBottom: 8 }}>
      <SkeletonFenix
        borderRadiusSize="medium"
        height="shape-size-action-05"
        width="25.75"
      />
    </div>
    <SkeletonFenix
      borderRadiusSize="medium"
      height="shape-size-action-05"
      width="shape-size-action-14"
    />
  </div>
);

const ScrollableProductCardSVertical = () => (
  <div
    data-testid="skeleton-food-product-card-s-revamp"
    style={{
      display: 'flex',
      overflow: 'hidden',
      overflowX: 'scroll',
      whiteSpace: 'nowrap',
      scrollbarWidth: 'none',
    }}
  >
    {[...Array(9)].map((_, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <ProductCardSVerticalSkeleton key={index} />
    ))}
  </div>
);

export const FoodSkeleton: FC<IFoodSkeleton> = memo(({ index, isRevamp }) =>
  isRevamp ? (
    <div data-testid="skeleton-food-revamp">
      <VendorRowSkeleton />
      <ScrollableProductCardSVertical />
    </div>
  ) : (
    <SkeletonPreRevampFood index={index} />
  ),
);

interface ISkeletonList {
  count: number;
  isRevamp: boolean;
}

const SkeletonList: React.FC<ISkeletonList> = ({ count, isRevamp }) => {
  const skeletons = Array.from({ length: count }, (_, i) => i);

  return (
    <ContainerSkeletonFood isRevamp={isRevamp}>
      {skeletons.map((skeleton, index) => (
        <FoodSkeleton index={index} key={skeleton} isRevamp={isRevamp} />
      ))}
    </ContainerSkeletonFood>
  );
};

export default memo(SkeletonList);
